import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, Activity, AlertCircle, CheckCircle, Clock, TrendingUp, Stethoscope, Download, Link2 } from 'lucide-react';
import moment from 'moment';
import 'moment/locale/es';
import { DonutChart } from '../components/DonutChart';
import { ExportExcelDialog } from '../components/ExportExcelDialog';
import { MonthYearPicker, monthRange } from '../components/MonthYearPicker';
import { UnlinkedSurgeriesDialog } from '../components/UnlinkedSurgeriesDialog';
import { SurgeryDialog } from '../components/SurgeryDialog';
moment.locale('es');

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PayBadge = ({ estado }) => {
  if (!estado) return null;
  const styles = {
    'Pagado Completo': 'bg-emerald-100 text-emerald-800',
    'Abono': 'bg-amber-100 text-amber-800',
    'Pendiente': 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[estado] || 'bg-gray-100 text-gray-700'}`}>
      {estado}
    </span>
  );
};

const PavilionDot = ({ pavilion }) => {
  const color = pavilion === 'Pabellón Capilar' ? '#0B8043' : '#D93025';
  return <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />;
};

const ProgressBar = ({ value, max, color }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
};

export function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [unlinkedOpen, setUnlinkedOpen] = useState(false);
  const [editingSurgery, setEditingSurgery] = useState(null);
  const now = moment();
  const [period, setPeriod] = useState({ month: now.month() + 1, year: now.year() });

  useEffect(() => {
    fetchStats(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.month, period.year]);

  const fetchStats = async (p) => {
    setLoading(true);
    try {
      const range = monthRange(p.month, p.year);
      const { data } = await axios.get(`${API_URL}/api/dashboard/stats`, {
        withCredentials: true,
        params: range,
      });
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-[#F3EBE1] rounded w-64"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-28 bg-[#F3EBE1] rounded-xl"></div>)}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[1,2].map(i => <div key={i} className="h-64 bg-[#F3EBE1] rounded-xl"></div>)}
          </div>
        </div>
      </div>
    );
  }

  const isCurrentMonth = period.month === now.month() + 1 && period.year === now.year();
  const periodLabel = moment(`${period.year}-${String(period.month).padStart(2, '0')}-01`).format('MMMM YYYY');

  // KPI cards adaptativos: si el periodo es el mes actual mostramos
  // "Hoy" y "Esta Semana"; si es otro mes esas cards no tienen sentido, así
  // que las reemplazamos por "Confirmadas" y "Sin Vincular" del periodo.
  const kpiCards = isCurrentMonth
    ? [
        { title: 'Cirugías Hoy', value: stats?.today_surgeries || 0, icon: Calendar, color: '#2B4C3B', bg: '#EDF5F0' },
        { title: 'Esta Semana', value: stats?.week_surgeries || 0, icon: Clock, color: '#1A73E8', bg: '#E8F0FE' },
        { title: 'Total del Mes', value: stats?.total_surgeries || 0, icon: Activity, color: '#D88D76', bg: '#FDF3EA' },
        { title: 'Pagos Confirmados', value: stats?.confirmed_payment || 0, icon: CheckCircle, color: '#0B8043', bg: '#E6F4EA' },
      ]
    : [
        { title: 'Total del Mes', value: stats?.total_surgeries || 0, icon: Activity, color: '#D88D76', bg: '#FDF3EA' },
        { title: 'Pagos Confirmados', value: stats?.confirmed_payment || 0, icon: CheckCircle, color: '#0B8043', bg: '#E6F4EA' },
        { title: 'Pendientes de Pago', value: stats?.pending_payment || 0, icon: AlertCircle, color: '#F59E0B', bg: '#FEF3C7' },
        { title: 'Sin Vincular Medilink', value: (stats?.payment_breakdown?.sin_info) || 0, icon: AlertCircle, color: '#6A736B', bg: '#F3EBE1' },
      ];

  const pb = stats?.pavilion_breakdown || {};
  const totalPav = (pb.capilar || 0) + (pb.estetica || 0);

  const pay = stats?.payment_breakdown || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1400px]" data-testid="dashboard-page">
      {/* Header con selector de periodo */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1C18] tracking-tight">
              Hola, {user?.name}
            </h1>
            <p className="text-[#6A736B] mt-1 text-sm capitalize" data-testid="dashboard-period-label">
              Panel de control · {periodLabel}
            </p>
          </div>
          {user?.permissions?.includes('export.excel') && (
            <Button
              data-testid="export-excel-btn"
              onClick={() => setExportOpen(true)}
              className="bg-[#2B4C3B] hover:bg-[#1F3A2C] text-white rounded-lg h-10 px-4 flex items-center gap-2 shadow-sm w-full sm:w-auto justify-center"
            >
              <Download size={16} />
              Exportar a Excel
            </Button>
          )}
        </div>
        <MonthYearPicker
          month={period.month}
          year={period.year}
          onChange={setPeriod}
        />
      </div>

      {/* Alerta: cirugías sin info Medilink */}
      {(pay.sin_info || 0) > 0 && (
        <Card
          className="bg-amber-50 border border-amber-200 rounded-xl"
          data-testid="sin-info-medilink-alert"
        >
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {pay.sin_info} {pay.sin_info === 1 ? 'cirugía' : 'cirugías'} sin vincular con Medilink
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                Vincula la atención Medilink desde el formulario de cirugía para habilitar el seguimiento de pagos.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setUnlinkedOpen(true)}
              data-testid="alert-manage-unlinked-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
            >
              <Link2 size={14} className="mr-1.5" /> Gestionar
            </Button>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <Card key={i} data-testid={`kpi-${i}`} className="bg-white rounded-xl border border-[#E8E5DD] hover:shadow-md transition-all">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-[#6A736B] uppercase tracking-wider">{stat.title}</span>
                  <div className="p-1.5 rounded-lg" style={{ backgroundColor: stat.bg }}>
                    <Icon size={16} style={{ color: stat.color }} strokeWidth={2} />
                  </div>
                </div>
                <div className="text-3xl font-bold text-[#1A1C18]">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Middle Row: Pavilion + Payment + States */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pabellones */}
        <Card className="bg-white rounded-xl border border-[#E8E5DD]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#1A1C18]">Cirugías por Pabellón</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#0B8043]" />
                  <span className="text-sm text-[#1A1C18]">Capilar</span>
                </div>
                <span className="text-sm font-bold text-[#1A1C18]">{pb.capilar || 0}</span>
              </div>
              <ProgressBar value={pb.capilar || 0} max={totalPav || 1} color="#0B8043" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#D93025]" />
                  <span className="text-sm text-[#1A1C18]">Cirugía Estética</span>
                </div>
                <span className="text-sm font-bold text-[#1A1C18]">{pb.estetica || 0}</span>
              </div>
              <ProgressBar value={pb.estetica || 0} max={totalPav || 1} color="#D93025" />
            </div>
            <div className="pt-2 border-t border-[#F0EDE8] text-xs text-[#6A736B]">
              Total: {totalPav} cirugías
            </div>
          </CardContent>
        </Card>

        {/* Pagos Medilink (Donut) - excluye "Sin info" que ya está en la alerta */}
        <Card className="bg-white rounded-xl border border-[#E8E5DD]" data-testid="payment-donut-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#1A1C18]">Resumen de Pagos</CardTitle>
          </CardHeader>
          <CardContent>
            <DonutChart
              title=""
              segments={[
                { label: 'Pagado Completo', value: pay.pagado_completo || 0, color: '#0B8043' },
                { label: 'Abono', value: pay.abono || 0, color: '#F59E0B' },
                { label: 'Pendiente', value: pay.pendiente || 0, color: '#EF4444' },
              ]}
            />
          </CardContent>
        </Card>

        {/* Estados (Donut) */}
        <Card className="bg-white rounded-xl border border-[#E8E5DD]" data-testid="state-donut-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#1A1C18]">Cirugías por Estado</CardTitle>
          </CardHeader>
          <CardContent>
            <DonutChart
              title=""
              segments={(stats?.state_counts || []).map((sc) => ({
                label: sc.state,
                value: sc.count || 0,
                color: sc.color || '#888',
              }))}
            />
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row: Upcoming + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Próximas Cirugías */}
        <Card className="bg-white rounded-xl border border-[#E8E5DD]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#1A1C18] flex items-center gap-2">
              <Stethoscope size={16} className="text-[#2B4C3B]" />
              Próximas Cirugías
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.upcoming?.length > 0 ? (
              <div className="space-y-0 divide-y divide-[#F0EDE8]">
                {stats.upcoming.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 py-2.5 first:pt-0" data-testid={`upcoming-${s.id}`}>
                    <PavilionDot pavilion={s.pavilion} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#1A1C18] truncate">{s.patient_name}</span>
                        <PayBadge estado={s.atencion_estado_pago} />
                      </div>
                      <p className="text-xs text-[#6A736B] truncate">{s.surgery_type}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-medium text-[#1A1C18]">{moment(s.date).format('ddd D MMM')}</p>
                      <p className="text-[11px] text-[#6A736B]">{s.time} – {s.end_time || ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#6A736B] py-4 text-center">No hay cirugías próximas</p>
            )}
          </CardContent>
        </Card>

        {/* Cirugías Recientes */}
        <Card className="bg-white rounded-xl border border-[#E8E5DD]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#1A1C18] flex items-center gap-2">
              <TrendingUp size={16} className="text-[#D88D76]" />
              Últimas Cirugías Agendadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent?.length > 0 ? (
              <div className="space-y-0 divide-y divide-[#F0EDE8]">
                {stats.recent.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 py-2.5 first:pt-0" data-testid={`recent-${s.id}`}>
                    <PavilionDot pavilion={s.pavilion} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#1A1C18] truncate">{s.patient_name}</span>
                        <PayBadge estado={s.atencion_estado_pago} />
                      </div>
                      <p className="text-xs text-[#6A736B] truncate">{s.surgery_type}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-medium text-[#1A1C18]">{moment(s.date).format('D MMM YYYY')}</p>
                      <p className="text-[11px] text-[#6A736B]">{s.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#6A736B] py-4 text-center">No hay cirugías registradas</p>
            )}
          </CardContent>
        </Card>
      </div>

      <ExportExcelDialog open={exportOpen} onClose={() => setExportOpen(false)} />
      <UnlinkedSurgeriesDialog
        open={unlinkedOpen}
        onClose={() => {
          setUnlinkedOpen(false);
          // Re-sincronizar el contador "Sin vincular Medilink" por si el
          // usuario vinculó alguna cirugía desde el modal.
          fetchStats(period);
        }}
        startDate={monthRange(period.month, period.year).start_date}
        endDate={monthRange(period.month, period.year).end_date}
        onEditSurgery={(surg) => {
          setUnlinkedOpen(false);
          setEditingSurgery(surg);
        }}
      />
      {editingSurgery && (
        <SurgeryDialog
          open={true}
          surgery={editingSurgery}
          onClose={() => {
            setEditingSurgery(null);
            fetchStats(period);
          }}
        />
      )}
    </div>
  );
}
